import {includes} from '@benchling/js-utils';
import $ from 'jquery';
import React from 'react';
import isEqual from 'react-fast-compare';

import DataConnector from 'connect/DataConnector';
import {SidepanelSection, STANDARD_SECTIONS} from 'constants/NavConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import getConfig from 'getConfig';
import FullScreenStore from 'nav/full-screen/FullScreenStore';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import * as SidebarConstants from 'sidebar/SidebarConstants';
import WorkspaceStore, {WorkspaceState} from 'sidebar/WorkspaceStore';
import EntityStore from 'stores/EntityStore';

import SidepanelStore from '../sidepanel/SidepanelStore';
import Workspace from './Workspace';

interface WorkspaceContainerProps {
  sidepanelSection: SidepanelSection | null;
  workspace: WorkspaceState;
  fullScreenOpen: boolean;
}

class WorkspaceContainerConnector extends DataConnector<{}, WorkspaceContainerProps> {
  stores = [FullScreenStore, SidepanelStore, WorkspaceStore];

  enhanceProps(): WorkspaceContainerProps {
    return {
      sidepanelSection: this.sidepanelStore().getSection(),
      workspace: this.workspaceStore().getState(),
      fullScreenOpen: Boolean(this.fullScreenStore().getCurrentScreen()),
    };
  }
}

/**
 * A light wrapper around the Workspace component that handles interacting with the WorkspaceStore
 * and the workspace's interaction with the editor's DOM elements.
 */
class WorkspaceContainer extends React.Component<WorkspaceContainerProps> {
  componentDidMount(): void {
    this.setBodyClass();
  }

  componentDidUpdate(): void {
    this.setBodyClass();
  }

  /**
   * If there are workspace items to render, have the .subworkspace (buffers, toolbar, statusbar)
   * render below .workspaceMount. Otherwise, position it to the top of the screen.
   */
  setBodyClass(): void {
    $(document.body).toggleClass('has-shown-workspace', !this._getIsWorkspaceHidden());
  }

  _getIsWorkspaceHidden(): boolean {
    const {fullScreenOpen, sidepanelSection, workspace} = this.props;

    let isStandardSection = includes(STANDARD_SECTIONS, sidepanelSection);
    if (sidepanelSection === SidebarConstants.Section.TEMPLATE_COLLECTIONS_SEARCH) {
      isStandardSection = getConfig('ENABLE_TEMPLATE_COLLECTIONS_IN_WORKSPACE');
    }

    return (
      workspace.workspaceItems.length === 0 || (sidepanelSection && !isStandardSection) || fullScreenOpen
    );
  }

  render(): JSX.Element {
    return (
      <div>
        {!this._getIsWorkspaceHidden() && (
          <Workspace
            openFile={FileBrowserActions.openFile}
            moveWorkspaceTab={FileBrowserActions.moveWorkspaceTab}
            onClearWorkspace={FileBrowserActions.clearWorkspace}
            onUpdateWorkspaceWidth={FileBrowserActions.updateWorkspaceWidth}
            currentUser={EntityStore.currentUser()}
            {...this.props.workspace}
          />
        )}
      </div>
    );
  }
}

const ConnectedWorkspaceContainer = WorkspaceContainerConnector.connect(WorkspaceContainer, {
  throttleUpdates: true,
  shouldPropagateNextState: (a, b) => !isEqual(a, b),
});

export function initWorkspaceContainer(): void {
  const workspaceNode = document.getElementsByClassName('workspaceMount')[0];
  renderToDOM(<ConnectedWorkspaceContainer />, workspaceNode);
}

export default ConnectedWorkspaceContainer;
